import classNames from "classnames";
import {
  AutomationListReviewDuplicates_OrgContactCrmDuplicatesQuery,
  AutomationListReviewDuplicates_OrgContactCrmDuplicatesQueryVariables,
  AutomationListReviewDuplicatesTable_ContactListFragmentDoc,
  CrmObjectType,
} from "../../../__generated__/graphql";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import { useParams } from "react-router-dom";
import { gql, useApolloClient } from "@apollo/client";
import AutomationListReviewDuplicatesTable, {
  AutomationListReviewDuplicatesTable_ContactList,
} from "./AutomationListReviewDuplicatesTable";
import { useFragment } from "../../../__generated__";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useWatch } from "react-hook-form";
import { useContext, useEffect, useState, useCallback } from "react";
import { AutomationListStateEnum } from "../../../routes/automationList/AutomationList";
import AutopilotReviewContactsContext from "../../../contexts/AutopilotReviewContactsContext";
import LoadingBar from "../../shared/LoadingBar";
import { loadingBarHeight } from "../../shared/LoadingBar";

const AutomationListReviewDuplicates_OrgContactCrmDuplicates = gql`
  query AutomationListReviewDuplicates_OrgContactCrmDuplicates(
    $inputs: [OrgContactCrmDuplicateInput!]!
  ) {
    orgContactCrmDuplicates(inputs: $inputs) {
      ...AutomationListReviewDuplicatesTable_ContactList
      orgContactCrmDuplicates {
        orgContact {
          id
        }
      }
    }
  }

  ${AutomationListReviewDuplicatesTable_ContactList}
`;

export const AutomationListReviewDuplicates_ContactList = gql`
  fragment AutomationListReviewDuplicates_ContactList on ContactList {
    id
    contacts {
      id
      org_contact {
        id
        first_name
        last_name
        company_name
      }
    }
  }
`;

const AutomationListReviewDuplicatesSchema = yup.object({
  crmObjectType: yup
    .string()
    .oneOf([CrmObjectType.Contact, CrmObjectType.Lead])
    .required(),
  formDuplicateActions: yup.array().of(
    yup.object({
      orgContactId: yup.number().required(),
      action: yup.string().oneOf(["skip", "create"]).required(),
      account: yup.string().when(["$crmObjectType", "action"], {
        is: (crmObjectType: string, action: string) =>
          crmObjectType === "contact" && action === "create",
        then: (schema) => schema.required("Account is required for contacts"),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
  ),
});

export type FormDuplicateActionsType = {
  crmObjectType: CrmObjectType;
  formDuplicateActions: {
    account?: string | null | undefined;
    orgContactId: number;
    action: "skip" | "create";
  }[];
};

const BATCH_SIZE = 10; // Number of contacts to load in each batch

const AutomationListReviewDuplicates = ({
  crmObject,
  setViewState,
  filteredContactIdsToOrgContactIds,
  setDuplicateActions,
}: {
  crmObject: CrmObjectType;
  setViewState: (viewState: AutomationListStateEnum) => void;
  filteredContactIdsToOrgContactIds: Map<number, number>;
  setDuplicateActions: (
    value: FormDuplicateActionsType["formDuplicateActions"],
  ) => void;
}) => {
  const { selectedContactIdsToOrgContactIds } = useContext(
    AutopilotReviewContactsContext,
  );
  const { listId } = useParams();
  const client = useApolloClient();

  // States for batch loading
  const [allOrgContactIds, setAllOrgContactIds] = useState<number[]>([]);
  const [processedBatches, setProcessedBatches] = useState<number>(0);
  const [totalBatches, setTotalBatches] = useState<number>(0);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);
  const [allDuplicatesData, setAllDuplicatesData] = useState<
    | AutomationListReviewDuplicates_OrgContactCrmDuplicatesQuery["orgContactCrmDuplicates"]
    | null
  >(null);
  const [error, setError] = useState<Error | null>(null);

  const { control, handleSubmit, setValue, reset } = useForm({
    resolver: yupResolver(AutomationListReviewDuplicatesSchema),
    defaultValues: {
      crmObjectType: crmObject,
      formDuplicateActions:
        [] as FormDuplicateActionsType["formDuplicateActions"],
    },
  });

  const formDuplicateActions = useWatch({
    control,
    name: "formDuplicateActions",
  });

  const resetBatches = useCallback(() => {
    const idArray = Array.from(
      selectedContactIdsToOrgContactIds.size > 0
        ? selectedContactIdsToOrgContactIds.values()
        : filteredContactIdsToOrgContactIds.values(),
    );

    setAllOrgContactIds(idArray);
    const batchCount = Math.ceil(idArray.length / BATCH_SIZE);
    setTotalBatches(batchCount);
    setProcessedBatches(0);
    setAllDuplicatesData(null);
    setAllLoaded(false);
    setIsInitialLoading(true);
  }, [selectedContactIdsToOrgContactIds, filteredContactIdsToOrgContactIds]);

  const fetchBatchDuplicates = useCallback(
    async ({
      batchIds,
      batchIndex,
    }: {
      batchIds: number[];
      batchIndex: number;
    }) => {
      try {
        const result = await client.query<
          AutomationListReviewDuplicates_OrgContactCrmDuplicatesQuery,
          AutomationListReviewDuplicates_OrgContactCrmDuplicatesQueryVariables
        >({
          query: AutomationListReviewDuplicates_OrgContactCrmDuplicates,
          variables: {
            inputs: batchIds.map((orgContactId) => ({ orgContactId })),
          },
          fetchPolicy: "network-only",
        });

        if (
          result.data?.orgContactCrmDuplicates?.orgContactCrmDuplicates &&
          !allLoaded
        ) {
          // Combine with previous results
          setAllDuplicatesData((prevData) => {
            if (!prevData) {
              return result.data.orgContactCrmDuplicates;
            }

            return {
              ...prevData,
              orgContactCrmDuplicates: [
                ...prevData.orgContactCrmDuplicates,
                ...result.data.orgContactCrmDuplicates.orgContactCrmDuplicates,
              ],
            };
          });

          // Track completed batches
          setProcessedBatches((prev) => {
            const newProcessedBatches = prev + 1;

            // Check if all batches are complete
            if (newProcessedBatches >= totalBatches) {
              setAllLoaded(true);
              setIsInitialLoading(false);
            }

            return newProcessedBatches;
          });
        }
      } catch (batchError) {
        setError(batchError as Error);
        // Still increment processed batches to avoid hanging
        setProcessedBatches((prev) => {
          const newProcessedBatches = prev + 1;
          if (newProcessedBatches >= totalBatches) {
            setAllLoaded(true);
            setIsInitialLoading(false);
          }
          return newProcessedBatches;
        });
      }
    },
    [totalBatches, allLoaded, client],
  );

  // Initialize contact IDs and calculate batches
  useEffect(() => {
    resetBatches();
  }, [resetBatches]);

  // Process batch by index using individual client queries
  const processBatch = useCallback(
    async (batchIndex: number) => {
      if (batchIndex < totalBatches) {
        const startIdx = batchIndex * BATCH_SIZE;
        const endIdx = Math.min(startIdx + BATCH_SIZE, allOrgContactIds.length);
        const batchIds = allOrgContactIds.slice(startIdx, endIdx);

        await fetchBatchDuplicates({ batchIds, batchIndex });
      }
    },
    [totalBatches, allOrgContactIds, fetchBatchDuplicates],
  );

  // Start staggered parallel batch processing when IDs are initialized
  useEffect(() => {
    if (allOrgContactIds.length > 0 && totalBatches > 0 && isInitialLoading) {
      const DELAY_MS = 2000; // 2 second delay between batch starts

      // Start each batch with a staggered delay
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        setTimeout(() => {
          processBatch(batchIndex);
        }, batchIndex * DELAY_MS);
      }

      setIsInitialLoading(false);
    }
  }, [allOrgContactIds, totalBatches, isInitialLoading, processBatch]);

  // Set form values when all duplicates are loaded
  useEffect(() => {
    if (allDuplicatesData?.orgContactCrmDuplicates) {
      reset({
        crmObjectType: crmObject,
        formDuplicateActions: allDuplicatesData.orgContactCrmDuplicates.map(
          (duplicate) => ({
            orgContactId: duplicate.orgContact.id!,
            action: "skip",
            account: null,
          }),
        ),
      });
    }
  }, [allDuplicatesData, reset, crmObject]);

  // Create fragment for table display
  const duplicatesTableContactList = useFragment(
    AutomationListReviewDuplicatesTable_ContactListFragmentDoc,
    allDuplicatesData ?? null,
  );

  if (error) {
    return (
      <div>
        Error fetching duplicates. Please contact{" "}
        <a className={classNames("text-red-500")} href="mailto:support@ciro.io">
          support@ciro.io
        </a>
        <div className={classNames("pt-4")}>
          <CiroButton
            analyticsField="Try deduplication again"
            analyticsProps={{
              listId,
            }}
            style={CiroButtonStyleEnum.LOUD}
            onClick={() => {
              resetBatches();
            }}
          >
            Try again
          </CiroButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={classNames(
          "flex",
          "w-full",
          "justify-between",
          "items-center",
        )}
      >
        <h1 className={classNames("text-2xl", "pb-6")}>Review duplicates</h1>
        <div className={classNames("flex", "gap-4")}>
          <CiroButton
            analyticsField="cancel-push-to-crm"
            disabled={isInitialLoading}
            analyticsProps={{
              listId,
            }}
            style={CiroButtonStyleEnum.INVERTED_LOUD}
            onClick={() => {
              setViewState(AutomationListStateEnum.Home);
            }}
          >
            Cancel
          </CiroButton>
          <CiroButton
            analyticsField="push-to-crm-continue"
            analyticsProps={{
              listId,
            }}
            disabled={isInitialLoading || !allLoaded}
            style={CiroButtonStyleEnum.LOUD}
            onClick={handleSubmit((data) => {
              setViewState(AutomationListStateEnum.FinalizePushToCrm);
              setDuplicateActions(data.formDuplicateActions);
            })}
          >
            Continue
          </CiroButton>
        </div>
      </div>
      <div className={classNames("w-full", "mt-6")}>
        {!allLoaded && (
          <div className={classNames("mb-4")}>
            <LoadingBar
              customFillPercentage={
                totalBatches ? processedBatches / totalBatches : 0
              }
              height={loadingBarHeight.SMALL}
            />
          </div>
        )}

        {/* Show "No duplicates" message when loading is complete and no duplicates found */}
        {allLoaded && formDuplicateActions.length === 0 && (
          <div className={classNames("text-center")}>
            No duplicates found! Please continue to review records to be
            exported.
          </div>
        )}

        {/* Show any loaded contacts, even while still loading more */}
        <AutomationListReviewDuplicatesTable
          setValue={setValue}
          isLoading={!allLoaded}
          formDuplicateActions={formDuplicateActions}
          contactList={duplicatesTableContactList ?? null}
          crmObjectType={crmObject}
        />
      </div>
    </>
  );
};

export default AutomationListReviewDuplicates;
